import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DetailsCard, ReactTable, useSnoozeTypeOptions } from "components";
import { getSnoozeStatusOptions } from "components/SnoozeModal/util";
import { Snooze } from "modules/CarDetails/components/Snooze";
import "modules/CarDetails/components/SnoozedItems/SnoozedItems.scss";
import { useCarDealer, useSnoozeItemsQuery } from "modules/CarDetails/hooks";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { SectionMenu } from "components";
import type { Snooze as SnoozeModel } from "models";
import type { ItemWithHistory } from "utils/itemsWithHistory";

type SnoozedItemsProps = {
  car_id: number;
  sectionMenu: SectionMenu;
  isExpanded: boolean;
};

const columnHelper = createColumnHelper<ItemWithHistory<SnoozeModel>>();

export const getSnoozedItemsCount = (snoozedItems: ItemWithHistory<SnoozeModel>[]) => snoozedItems.length;

export const SnoozedItems = ({ car_id, sectionMenu, isExpanded }: SnoozedItemsProps) => {
  const { t } = useTranslation();

  const carDealer = useCarDealer();
  const { data: snoozedItems = [] } = useSnoozeItemsQuery(car_id);
  const { snoozeTypeOptions } = useSnoozeTypeOptions(carDealer);
  const { snoozeStatusOptions } = getSnoozeStatusOptions(t);

  if (!sectionMenu.count || !snoozedItems.length) return null;

  const columns = [
    columnHelper.accessor(row => row, {
      id: "date",
      header: t("v8_appointment_date", "Appointment Date"),
      cell: ({ row: { original } }) => (original.appointment?.created_on ? formatDate(original.appointment.created_on, DATE_FORMATS_FNS.dateMonthYear) : "")
    }),
    columnHelper.accessor(row => row, {
      id: "wo",
      header: "WO #",
      cell: ({
        row: {
          original: { appointment_id, appointment }
        }
      }) => (
        <Popup
          hoverable
          content={appointment?.wo_nr ?? ""}
          trigger={
            <p className="ellipsis">
              <Link to={`${PATHS.APPOINTMENTS}/${appointment_id}`} target="_blank">
                <Icon className="arrow up right from square" />
                {appointment?.wo_nr}
              </Link>
            </p>
          }
        />
      )
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_date",
      header: t("v8_snooze_date", "Snooze Date"),
      cell: ({ row: { original } }) => <span> {original.snooze_date ? formatDate(original.snooze_date, DATE_FORMATS_FNS.dateMonthYear) : ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "snooze_status",
      header: t("v8_snooze_status", "Snooze Status"),
      cell: ({ row: { original } }) => <span> {snoozeStatusOptions.find(snooze => snooze.value === original.snooze_status_id)?.text || ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "task",
      header: t("v8_task", "Task"),
      cell: ({ row: { original } }) => <span> {snoozeTypeOptions.find(snooze => snooze.value === original.snooze_type_id)?.text || ""} </span>
    }),
    columnHelper.accessor(row => row, {
      id: "intervention",
      header: t("v8_intervention_question", "Intervention/Question"),
      cell: ({ row: { original } }) => (
        <Popup
          hoverable
          content={original.question_result?.title || original.intervention?.title}
          trigger={<p className="ellipsis">{original.question_result?.title || original.intervention?.title}</p>}
        />
      ),
      size: 180
    }),
    columnHelper.accessor(row => row, {
      id: "note",
      header: t("v8_note", "Note"),
      cell: ({ row: { original } }) => <Popup hoverable content={original.note} trigger={<p className="ellipsis">{original.note}</p>} />
    }),
    columnHelper.accessor(row => row, {
      id: "actions",
      header: "",
      cell: ({ row: { original } }) => <Snooze snoozeItemsWithHistory={original} showOwnerInfo />
    })
  ];

  return (
    <DetailsCard isExpanded={isExpanded} sectionMenuId={sectionMenu.id} icon={sectionMenu.icon} title={`${sectionMenu.title} (${sectionMenu.count})`}>
      <div className="SnoozedItems">
        <ReactTable columns={columns} data={snoozedItems} />
      </div>
    </DetailsCard>
  );
};
